import { doc, collection, runTransaction, getDoc, writeBatch, serverTimestamp, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import usePointsStore from "../StatesStore/PointsTable";
import { db } from '../FirebaseConfig';
import { toast } from 'react-toastify';
import { toastOptions } from '../lib/toastOptions';

class PointsService {
    static getPointsConfig() {
        // Get pointsTable from Zustand store
        const pointsTable = usePointsStore.getState().pointsTable;

        if (!pointsTable) {
            throw new Error('No points configuration found in store');
        }

        // Transform the config into a more usable format
        const config = {
            dailyTotalPointsLimit: pointsTable.dailyTotalPointsLimit || 30
        };

        // Extract all activities
        for (const key in pointsTable) {
            if (typeof pointsTable[key] === 'object' && pointsTable[key].pointsPerAction !== undefined) {
                config[key] = {
                    pointsPerAction: pointsTable[key].pointsPerAction,
                    maxPoints: pointsTable[key].maxPoints,
                    days: pointsTable[key].days,
                    description: pointsTable[key].description
                };
            }
        }

        return config;
    }

    static async getLastActivityLog(userId, activityType) {
        const logsRef = collection(db, 'users', userId, 'pointsLog');
        const q = query(
            logsRef,
            where('activityType', '==', activityType),
            orderBy('timestamp', 'desc'),
            limit(1)
        );
        
        const snapshot = await getDocs(q);
        if (snapshot.empty) return null;
        
        return snapshot.docs[0].data();
    }

    static async awardPoints(userId, activityType,fields = {}) {
        const config = this.getPointsConfig();
        const activityConfig = config[activityType];

        if (!activityConfig) {
            toast.error(
                `Invalid activity type: ${activityType}`,
                toastOptions
            );
            throw new Error(`Invalid activity type: ${activityType}`);
        }

        const today = new Date();
        const todayDateStr = today.toLocaleDateString('en-CA');
        const userPointsRef = doc(db, 'userPoints', `${userId}-${todayDateStr}`);
        const userRef = doc(db, 'users', userId);

        try {
            // First, get all necessary documents
            const [dailyPointsDoc, userDoc] = await Promise.all([
                getDoc(userPointsRef),
                getDoc(userRef)
            ]);

            // Prepare the data
            let dailyPointsData = dailyPointsDoc.exists() ? dailyPointsDoc.data() : {
                userId,
                date: todayDateStr,
                dailyTotal: 0,
                activities: {}
            };

            // Initialize activity if it doesn't exist in daily data
            if (!dailyPointsData.activities[activityType]) {
                dailyPointsData.activities[activityType] = {
                    current: 0
                };
            }

            // Check activity limit based on days configuration
            let activityCurrent = 0;
            if (activityConfig.days > 1) {
                // For multi-day activities, check pointsLog for recent activity
                const lastActivity = await this.getLastActivityLog(userId, activityType);
                if (lastActivity) {
                    const lastActivityDate = lastActivity.timestamp.toDate();
                    const daysSinceLastActivity = (today - lastActivityDate) / (24 * 60 * 60 * 1000);
                    
                    if (daysSinceLastActivity < activityConfig.days) {
                        // Sum points from recent activities within the time window
                        const logsRef = collection(db, 'users', userId, 'pointsLog');
                        const startDate = new Date(today.getTime() - (activityConfig.days * 24 * 60 * 60 * 1000));
                        const q = query(
                            logsRef,
                            where('activityType', '==', activityType),
                            where('points', '>', 0), 
                            where('timestamp', '>', startDate)
                        );
                        
                        const snapshot = await getDocs(q);
                        activityCurrent = snapshot.docs.reduce((sum, doc) => sum + doc.data().points, 0);
                    }
                }
            } else {
                // For daily activities, just use today's count
                activityCurrent = dailyPointsData.activities[activityType].current || 0;
            }

            // Check activity limit
            if (activityCurrent >= activityConfig.maxPoints) {
                toast.error(
                    `Maximum points for ${activityType} reached (${activityConfig.maxPoints} points every ${activityConfig.days} days)`,
                    toastOptions
                );
                throw new Error(`Maximum points for ${activityType} reached`);
            }

            // Check daily total limit
            if (dailyPointsData.dailyTotal >= config.dailyTotalPointsLimit) {
                toast.error(
                    "Daily total point limit reached",
                    toastOptions
                );
                throw new Error('Daily total point limit reached');
            }

            // Calculate points to award
            const remainingDaily = config.dailyTotalPointsLimit - dailyPointsData.dailyTotal;
            const remainingActivity = activityConfig.maxPoints - activityCurrent;
            const pointsToAward = Math.min(
                activityConfig.pointsPerAction,
                remainingDaily,
                remainingActivity
            );

            if (pointsToAward <= 0) {
                toast.error(
                    "No points can be awarded",
                    toastOptions
                );
                throw new Error('No points can be awarded');
            }

            // Use a batch write
            const batch = writeBatch(db);

            // Update daily points data
            dailyPointsData.activities[activityType].current += pointsToAward;
            dailyPointsData.dailyTotal += pointsToAward;
            batch.set(userPointsRef, dailyPointsData);

            // Update lifetime points
            const currentPoints = userDoc.exists() ? (userDoc.data().points || 0) : 0;
            batch.set(userRef, {
                points: currentPoints + pointsToAward
            }, { merge: true });

            // Record transaction
            const transactionRef = doc(collection(db, 'users', userId, 'pointsLog'));
            batch.set(transactionRef, {
                activityType,
                points: pointsToAward,
                timestamp: serverTimestamp(),
                date: todayDateStr,
                   ...fields
            });

            await batch.commit();
            toast.success(
                "Points awarded",
                toastOptions
            );
            return pointsToAward;
        } catch (error) {
            console.log('error',error);
            
            throw error;
        }
    }
    static async deductPoints(userId, activityType, fields = {}) {
    const config = this.getPointsConfig();
    const activityConfig = config[activityType];
        console.log('Inside deduct points');
        
    if (!activityConfig) {
        toast.error(
            `Invalid activity type: ${activityType}`,
            toastOptions
        );
        throw new Error(`Invalid activity type: ${activityType}`);
    }

    const today = new Date();
    const todayDateStr = today.toLocaleDateString('en-CA');
    const userPointsRef = doc(db, 'userPoints', `${userId}-${todayDateStr}`);
    const userRef = doc(db, 'users', userId);

    try {
        // Find the original points log entry to reverse
        const logsRef = collection(db, 'users', userId, 'pointsLog');
        let q = query(
            logsRef,
            where('activityType', '==', activityType),
            where('points', '>', 0), 
            orderBy('timestamp', 'desc')
        );
        console.log('fields inside deduct points',fields);
        
        // Add field conditions if provided
        if (fields && typeof fields === 'object') {
            Object.keys(fields).forEach((key) => {
                if (fields[key] !== null && fields[key] !== undefined) {
                    q = query(q, where(key, '==', fields[key]));
                }
            });
        }

        const snapshot = await getDocs(q);
        if (snapshot.empty) {
            toast.error(
                "No matching activity found to reverse",
                toastOptions
            );
            throw new Error('No matching activity found to reverse');
        }

        // Get the most recent matching activity
        const originalActivityDoc = snapshot.docs[0];
        const originalActivity = originalActivityDoc.data();
        
        // Check if this is already reversed
         if (originalActivity.points <= 0) {
            toast.error(
                "This activity has already been reversed",
                toastOptions
            );
            throw new Error('Activity already reversed');
        }

        const pointsToDeduct = originalActivity.points;

        // Get current points data
        const [dailyPointsDoc, userDoc] = await Promise.all([
            getDoc(userPointsRef),
            getDoc(userRef)
        ]);

        let dailyPointsData = dailyPointsDoc.exists() ? dailyPointsDoc.data() : {
            userId,
            date: todayDateStr,
            dailyTotal: 0,
            activities: {}
        };

        // Initialize activity if it doesn't exist in daily data
        if (!dailyPointsData.activities[activityType]) {
            dailyPointsData.activities[activityType] = {
                current: 0
            };
        }

        // Verify we're not deducting more than available
        if (dailyPointsData.activities[activityType].current < pointsToDeduct) {
            dailyPointsData.activities[activityType].current = 0;
        } else {
            dailyPointsData.activities[activityType].current -= pointsToDeduct;
        }

        if (dailyPointsData.dailyTotal < pointsToDeduct) {
            dailyPointsData.dailyTotal = 0;
        } else {
            dailyPointsData.dailyTotal -= pointsToDeduct;
        }

        // Use a batch write
        const batch = writeBatch(db);

        // Update daily points data
        batch.set(userPointsRef, dailyPointsData);

        // Update lifetime points
        const currentPoints = userDoc.exists() ? (userDoc.data().points || 0) : 0;
        const newPoints = Math.max(0, currentPoints - pointsToDeduct); // Prevent negative points
        batch.set(userRef, {
            points: newPoints
        }, { merge: true });

        // Update the original transaction to mark it as reversed
        const originalTransactionRef = doc(db, 'users', userId, 'pointsLog', originalActivityDoc.id);
        batch.update(originalTransactionRef, {
            isReversed: true,
            points: -pointsToDeduct, // Set points to negative
            reversedAt: serverTimestamp()
        });

        await batch.commit();
        toast.success(
            "Points deducted",
            toastOptions
        );
        return pointsToDeduct;
    } catch (error) {
        console.log('error',error);
        
        throw error;
    }
}
    static async getUserPoints(userId) {
        const today = new Date();
        const todayDateStr = today.toLocaleDateString('en-CA');
        console.log('todayDateStr',todayDateStr);
        
        const [userDoc, dailyPointsDoc] = await Promise.all([
            getDoc(doc(db, 'users', userId)),
            getDoc(doc(db, 'userPoints', `${userId}-${todayDateStr}`))
        ]);

        const config = this.getPointsConfig();
        const lifetimePoints = userDoc.exists() ? (userDoc.data().points || 0) : 0;

        let dailyPoints = 0;
        let activityPoints = {};

        // Get today's points if available
        if (dailyPointsDoc.exists()) {
            const dailyPointsData = dailyPointsDoc.data();
            dailyPoints = dailyPointsData.dailyTotal || 0;
            
            // Initialize activity points from today's data
             for (const key in dailyPointsData.activities) {
            if (config[key]) {
                activityPoints[key] = {
                    dailyCurrent: dailyPointsData.activities[key].current || 0,
                    pointsPerAction: config[key].pointsPerAction,
                    // Add these properties for all activities
                    current: config[key].days === 1 ? dailyPointsData.activities[key].current || 0 : 0,
                    maxPoints: config[key].maxPoints,
                    days: config[key].days,
                    lastReset: config[key].days === 1 ? today.toISOString() : null
                };
            }
        }
        }

        // For activities with days > 1, we need to check pointsLog
        const activityPromises = Object.keys(config)
            .filter(key => config[key].pointsPerAction !== undefined)
            .map(async (key) => {
                if (config[key].days > 1) {
                    const lastActivity = await this.getLastActivityLog(userId, key);
                    if (lastActivity) {
                        const lastActivityDate = lastActivity.timestamp.toDate();
                        const daysSinceLastActivity = (today - lastActivityDate) / (24 * 60 * 60 * 1000);
                        
                        if (daysSinceLastActivity < config[key].days) {
                            // Sum points from recent activities within the time window
                            const logsRef = collection(db, 'users', userId, 'pointsLog');
                            const startDate = new Date(today.getTime() - (config[key].days * 24 * 60 * 60 * 1000));
                            const q = query(
                                logsRef,
                                where('activityType', '==', key),
                                where('timestamp', '>', startDate)
                            );
                            
                            const snapshot = await getDocs(q);
                            const current = snapshot.docs.reduce((sum, doc) => sum + doc.data().points, 0);
                            
                            activityPoints[key] = {
                                ...(activityPoints[key] || {}),
                                current,
                                lastReset: startDate.toISOString(),
                                maxPoints: config[key].maxPoints,
                                days: config[key].days
                            };
                        }
                    }
                }
                
                // Ensure all activities are represented in the response
                if (!activityPoints[key]) {
                    activityPoints[key] = {
                        current: 0,
                        lastReset: null,
                        maxPoints: config[key].maxPoints,
                        days: config[key].days,
                        pointsPerAction: config[key].pointsPerAction,
                        dailyCurrent: activityPoints[key]?.dailyCurrent || 0
                    };
                }
            });

        await Promise.all(activityPromises);

        return {
            lifetimePoints,
            dailyPoints,
            dailyMax: config.dailyTotalPointsLimit,
            activityPoints
        };
    }
}

export default PointsService;